/** ペインの幅（px）。チャットとインスペクタの 2 つだけを覚える。 */
export interface PaneWidths {
  chat: number;
  inspector: number;
}

export type PaneName = keyof PaneWidths;

/** 覚える先（U4）。Workspace ごとには分けない（画面の好みなので 1 つで足りる）。 */
export const PANE_WIDTHS_KEY = 'studio.paneWidths';

/** 初期幅は現状の CSS の値（`.studio-chat` の 325px・`.studio-inspector` の 320px）。 */
export const DEFAULT_PANE_WIDTHS: PaneWidths = { chat: 325, inspector: 320 };

/** 最小幅（U4）。これより狭いと入力欄と質問カードが読めなくなる。 */
export const MIN_PANE_WIDTHS: PaneWidths = { chat: 280, inspector: 240 };

/** 最大幅（U4 への追記）。窓の幅は見ず固定の px で止めるだけ（成果物の列が消えないようにする）。 */
export const MAX_PANE_WIDTHS: PaneWidths = { chat: 720, inspector: 640 };

/** 最小と最大に収めた整数の px。数でない値は既定に落とす。 */
export function clampPaneWidth(pane: PaneName, px: number): number {
  if (!Number.isFinite(px)) return DEFAULT_PANE_WIDTHS[pane];
  return Math.min(MAX_PANE_WIDTHS[pane], Math.max(MIN_PANE_WIDTHS[pane], Math.round(px)));
}

/** 覚えている幅。読めない・壊れている値は既定にする（localStorage が使えなくても投げない）。 */
export function readPaneWidths(): PaneWidths {
  try {
    const raw = window.localStorage.getItem(PANE_WIDTHS_KEY);
    if (raw === null) return DEFAULT_PANE_WIDTHS;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return DEFAULT_PANE_WIDTHS;
    const record = parsed as Record<string, unknown>;
    return { chat: onePane(record['chat'], 'chat'), inspector: onePane(record['inspector'], 'inspector') };
  } catch {
    return DEFAULT_PANE_WIDTHS;
  }
}

/** 覚える。書けない環境（private mode など）でも画面は動くので握りつぶす。 */
export function writePaneWidths(widths: PaneWidths): void {
  try {
    window.localStorage.setItem(PANE_WIDTHS_KEY, JSON.stringify(widths));
  } catch {
    return;
  }
}

function onePane(value: unknown, pane: PaneName): number {
  return typeof value === 'number' ? clampPaneWidth(pane, value) : DEFAULT_PANE_WIDTHS[pane];
}
